import { useCallback } from 'react';

import { useApolloClient } from '@apollo/client';

/**
 * Single source of truth for the analytics index ROOT_QUERY fields that use a
 * caching fetchPolicy (see the *Metrics field policies in InitAppWithApollo).
 *
 * Consumed in two places that MUST stay in sync:
 *  1. InitAppWithApollo builds a `merge: false` field policy per entry.
 *  2. useInvalidateAnalyticsCache evicts each entry when an analytics setting
 *     changes, so cached snapshots don't outlive the config they were fetched
 *     under.
 */
export const ANALYTICS_CACHED_QUERY_FIELDS = [
  'campaignsMetrics',
  'creativesMetrics',
  'groupedCampaignsMetrics',
  'groupedCreativesMetrics',
  'experimentsMetrics',
  'experimentContactsMetrics',
  'segmentGroupsMetrics',
  'subjectlinesMetrics',
] as const;

/**
 * Returns a callback that voids every cached analytics index snapshot. Call it
 * after any AnalyticsSettings mutation succeeds: `cache-first` never refetches
 * on remount, so without this an index page would keep serving numbers computed
 * under the old setting until a hard refresh or a new filter (new variables).
 *
 * Evicting the ROOT_QUERY field with no args drops every argument variation
 * (all filter/date/groupBy slots) in one call.
 */
export const useInvalidateAnalyticsCache = (): (() => void) => {
  const client = useApolloClient();

  return useCallback(() => {
    ANALYTICS_CACHED_QUERY_FIELDS.forEach((fieldName) => {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName });
    });
    client.cache.gc();
  }, [client]);
};
